import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate} from '../hooks/axiosInstance';

// Async thunks
export const getUserSettings = createAsyncThunk(
  'settings/getUserSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get('/settings');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch settings');
    }
  }
);

export const updateMaintenance = createAsyncThunk(
  'settings/updateMaintenance',
  async (maintenance, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.put('/settings/maintenance', { maintenance });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to update maintenance mode');
    }
  }
);

// Gmail SMTP thunks
export const getGmailSmtp = createAsyncThunk(
  'settings/getGmailSmtp',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get('/settings/gmail-smtp');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch Gmail SMTP settings');
    }
  }
);

export const saveGmailCredentials = createAsyncThunk(
  'settings/saveGmailCredentials',
  async ({ clientId, clientSecret, redirectUri }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.put('/settings/gmail-smtp', { clientId, clientSecret, redirectUri });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to save Gmail credentials');
    }
  }
);

export const getGmailAuthUrl = createAsyncThunk(
  'settings/getGmailAuthUrl',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get('/settings/gmail-smtp/auth-url');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to get authorization URL');
    }
  }
);

export const handleGmailCallback = createAsyncThunk(
  'settings/handleGmailCallback',
  async ({ code, state }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.post('/settings/gmail-smtp/callback', { code, state });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to connect Gmail');
    }
  }
);

export const disconnectGmail = createAsyncThunk(
  'settings/disconnectGmail',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.post('/settings/gmail-smtp/disconnect');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to disconnect Gmail');
    }
  }
);

export const deleteGmailSmtp = createAsyncThunk(
  'settings/deleteGmailSmtp',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.delete('/settings/gmail-smtp');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete Gmail SMTP settings');
    }
  }
);

export const sendTestEmail = createAsyncThunk(
  'settings/sendTestEmail',
  async (toEmail, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.post('/settings/gmail-smtp/test', {
        to: toEmail,
        subject: 'Test Email - Gmail SMTP Configuration',
        body: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #10b981;">Gmail SMTP Test Successful!</h2>
            <p>This is a test email to confirm your Gmail SMTP configuration is working correctly.</p>
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 20px 0;" />
            <p style="color: #6b7280; font-size: 14px;">
              Sent from your Portfolio Admin Panel
            </p>
          </div>
        `
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to send test email');
    }
  }
);

const initialState = {
  settings: null,
  maintenance: false,
  gmailSmtp: {
    clientId: null,
    clientSecret: null,
    redirectUri: null,
    isConnected: false,
    connectedEmail: null
  },
  authUrl: null,
  loading: false,
  smtpLoading: false,
  connectLoading: false,
  testEmailLoading: false,
  error: null,
  success: null,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.success = null;
    },
    setMaintenance: (state, action) => {
      state.maintenance = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get user settings
      .addCase(getUserSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
        state.maintenance = action.payload.maintenance;
        state.error = null;
      })
      .addCase(getUserSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update maintenance mode
      .addCase(updateMaintenance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMaintenance.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenance = action.payload.maintenance;
        state.success = `Maintenance mode ${action.payload.maintenance ? 'enabled' : 'disabled'}`;
        state.error = null;
      })
      .addCase(updateMaintenance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Gmail SMTP settings
      .addCase(getGmailSmtp.pending, (state) => {
        state.smtpLoading = true;
        state.error = null;
      })
      .addCase(getGmailSmtp.fulfilled, (state, action) => {
        state.smtpLoading = false;
        state.gmailSmtp = action.payload.gmailSmtp;
        state.error = null;
      })
      .addCase(getGmailSmtp.rejected, (state, action) => {
        state.smtpLoading = false;
        state.error = action.payload;
      })
      // Save Gmail credentials
      .addCase(saveGmailCredentials.pending, (state) => {
        state.smtpLoading = true;
        state.error = null;
      })
      .addCase(saveGmailCredentials.fulfilled, (state, action) => {
        state.smtpLoading = false;
        state.gmailSmtp = action.payload.gmailSmtp;
        state.success = action.payload.msg;
        state.error = null;
      })
      .addCase(saveGmailCredentials.rejected, (state, action) => {
        state.smtpLoading = false;
        state.error = action.payload;
      })
      // Get Gmail auth URL
      .addCase(getGmailAuthUrl.pending, (state) => {
        state.connectLoading = true;
        state.error = null;
      })
      .addCase(getGmailAuthUrl.fulfilled, (state, action) => {
        state.connectLoading = false;
        state.authUrl = action.payload.authUrl;
        state.error = null;
      })
      .addCase(getGmailAuthUrl.rejected, (state, action) => {
        state.connectLoading = false;
        state.error = action.payload;
      })
      // Handle Gmail callback
      .addCase(handleGmailCallback.pending, (state) => {
        state.connectLoading = true;
        state.error = null;
      })
      .addCase(handleGmailCallback.fulfilled, (state, action) => {
        state.connectLoading = false;
        state.gmailSmtp = {
          ...state.gmailSmtp,
          isConnected: action.payload.gmailSmtp.isConnected,
          connectedEmail: action.payload.gmailSmtp.connectedEmail
        };
        state.success = action.payload.msg;
        state.error = null;
      })
      .addCase(handleGmailCallback.rejected, (state, action) => {
        state.connectLoading = false;
        state.error = action.payload;
      })
      // Disconnect Gmail
      .addCase(disconnectGmail.pending, (state) => {
        state.connectLoading = true;
        state.error = null;
      })
      .addCase(disconnectGmail.fulfilled, (state, action) => {
        state.connectLoading = false;
        state.gmailSmtp = action.payload.gmailSmtp;
        state.success = action.payload.msg;
        state.error = null;
      })
      .addCase(disconnectGmail.rejected, (state, action) => {
        state.connectLoading = false;
        state.error = action.payload;
      })
      // Delete Gmail SMTP settings
      .addCase(deleteGmailSmtp.pending, (state) => {
        state.smtpLoading = true;
        state.error = null;
      })
      .addCase(deleteGmailSmtp.fulfilled, (state) => {
        state.smtpLoading = false;
        state.gmailSmtp = {
          clientId: null,
          clientSecret: null,
          redirectUri: null,
          isConnected: false,
          connectedEmail: null
        };
        state.success = 'Gmail SMTP settings deleted successfully';
        state.error = null;
      })
      .addCase(deleteGmailSmtp.rejected, (state, action) => {
        state.smtpLoading = false;
        state.error = action.payload;
      })
      // Send test email
      .addCase(sendTestEmail.pending, (state) => {
        state.testEmailLoading = true;
        state.error = null;
      })
      .addCase(sendTestEmail.fulfilled, (state, action) => {
        state.testEmailLoading = false;
        state.success = action.payload.msg;
        state.error = null;
      })
      .addCase(sendTestEmail.rejected, (state, action) => {
        state.testEmailLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearMessages, setMaintenance } = settingsSlice.actions;
export default settingsSlice.reducer;